import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "min-w-0 flex-1 bg-white" }
const _hoisted_2 = {
  key: 0,
  class: "flex h-full min-h-[28rem] flex-col items-center justify-center gap-3 px-6 text-center text-slate-400"
}
const _hoisted_3 = {
  key: 1,
  class: "flex h-full min-h-0 flex-col"
}
const _hoisted_4 = { class: "flex flex-col gap-4 border-b border-slate-200 px-5 py-5 sm:flex-row sm:items-start sm:justify-between lg:px-7" }
const _hoisted_5 = { class: "min-w-0" }
const _hoisted_6 = { class: "text-xs font-bold uppercase tracking-[0.12em] text-indigo-500" }
const _hoisted_7 = { class: "mt-1 flex flex-wrap items-center gap-2" }
const _hoisted_8 = { class: "truncate text-xl font-bold text-slate-800" }
const _hoisted_9 = { class: "text-sm text-slate-400" }
const _hoisted_10 = { class: "mt-1 truncate text-sm text-slate-500" }
const _hoisted_11 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_12 = { class: "min-h-0 flex-1 overflow-y-auto bg-slate-50/40 p-4 lg:p-7" }
const _hoisted_13 = { class: "mx-auto max-w-5xl space-y-4" }
const _hoisted_14 = {
  key: 0,
  class: "flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
}
const _hoisted_15 = {
  key: 1,
  class: "grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
}
const _hoisted_16 = {
  key: 2,
  class: "rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
}
const _hoisted_17 = { class: "mb-4 flex items-center gap-2" }
const _hoisted_18 = { class: "grid gap-3 sm:grid-cols-2 lg:grid-cols-4" }
const _hoisted_19 = { class: "text-xs font-medium text-slate-500" }
const _hoisted_20 = { class: "rounded-xl border border-slate-200 bg-white p-5 shadow-sm" }
const _hoisted_21 = { class: "flex items-center gap-2" }
const _hoisted_22 = { class: "mt-3 text-sm leading-relaxed text-slate-600" }

import { computed } from "vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import RenewalActivityLog from "@/components/dashboard/renewals/RenewalActivityLog.vue";
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileClock,
  Pencil,
  RefreshCw,
  TriangleAlert,
  XCircle,
} from "@lucide/vue";
import type { NormalizedClient } from "@/composables/useClientsQuery";
import type { RenewalTrackingDetail } from "@/composables/useRenewalTracking";


export default /*@__PURE__*/_defineComponent({
  __name: 'RenewalAccountDetails',
  props: {
    client: {},
    detailClient: {},
    isLoading: { type: Boolean },
    isError: { type: Boolean },
    canEdit: { type: Boolean },
    sectionLabel: {},
    accountStatus: {}
  },
  emits: ["open-profile", "edit", "retry"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const renewalNote = computed(() =>
  String(
    props.detailClient?.renewal_note ||
      props.client?.raw?.renewal_note ||
      ""
  ).trim()
);

const renewalMilestones = computed(() => {
  const client = props.client;
  if (!client) return [];

  return [
    {
      label: "Onboarding date",
      value: formatRenewalDate(client.onboardingDateValue),
    },
    {
      label: "Subscription start",
      value: formatRenewalDate(client.subscriptionStartDateValue),
    },
    {
      label: "Renewal date",
      value: formatRenewalDate(client.renewalDateValue),
    },
    {
      label: "Payment due date",
      value: formatRenewalDate(client.paymentDueDateValue),
    },
    {
      label: "Renewal start",
      value: formatRenewalDate(client.renewalStartDateValue),
    },
    {
      label: "Renewal end",
      value: formatRenewalDate(client.renewalEndDateValue),
    },
    {
      label: props.accountStatus ? "Account status" : "Renewal status",
      value: getRenewalStatusLabel(client),
    },
  ];
});

function getRenewalStatusLabel(client: NormalizedClient | null) {
  if (props.accountStatus) return props.accountStatus.label;
  if (!client) return "Active";

  const value =
    client.raw?.renewal_status ||
    client.raw?.renewal_summary?.derived_status ||
    client.renewalStatus;

  return String(value || "Active")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

const renewalStatusLabel = computed(() => getRenewalStatusLabel(props.client));

function formatRenewalDate(value: string | null | undefined) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "mm/dd/yyyy";

  const datePart = rawValue.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (match) return [match[2], match[3], match[1]].join("/");

  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) return "mm/dd/yyyy";

  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function statusClass(client: NormalizedClient) {
  if (props.accountStatus) return props.accountStatus.badgeClass;
  if (client.renewalStatusTone === "expired") {
    return "border-red-200 bg-red-50 text-red-700";
  }

  if (client.renewalStatusTone === "due-soon") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }

  if (client.renewalStatusTone === "renewed") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  return "border-indigo-200 bg-indigo-50 text-indigo-700";
}

function statusIcon(client: NormalizedClient) {
  if (props.accountStatus) return Clock3;
  if (client.renewalStatusTone === "expired") return XCircle;
  if (client.renewalStatusTone === "due-soon") return TriangleAlert;
  if (client.renewalStatusTone === "renewed") return CheckCircle2;
  return Clock3;
}

function milestoneClass(value: string) {
  return value === "mm/dd/yyyy"
    ? "text-slate-400"
    : "text-slate-800";
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    (!__props.client)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(FileClock), {
            class: "size-12",
            "aria-hidden": "true"
          }),
          _cache[3] || (_cache[3] = _createElementVNode("div", null, [
            _createElementVNode("p", { class: "text-base font-semibold text-slate-600" }, " Select an account "),
            _createElementVNode("p", { class: "mt-1 text-sm" }, " Choose an account to view its renewal details. ")
          ], -1))
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("header", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("p", _hoisted_6, _toDisplayString(__props.sectionLabel || "Renewal Tracking"), 1),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("h1", _hoisted_8, _toDisplayString(__props.client.companyName), 1),
                _createElementVNode("span", _hoisted_9, _toDisplayString(__props.client.referenceCode), 1)
              ]),
              _createElementVNode("p", _hoisted_10, _toDisplayString(__props.client.email), 1),
              _cache[4] || (_cache[4] = _createElementVNode("p", { class: "mt-3 max-w-xl text-sm text-slate-500" }, " Update renewal milestones, payment due dates, and manual renewal notes. ", -1))
            ]),
            _createElementVNode("div", _hoisted_11, [
              _createVNode(_unref(Badge), {
                variant: "outline",
                class: _normalizeClass(["px-2.5 py-1 text-xs font-semibold", statusClass(__props.client)])
              }, {
                default: _withCtx(() => [
                  (_openBlock(), _createBlock(_resolveDynamicComponent(statusIcon(__props.client)), {
                    class: "mr-1.5 size-3.5",
                    "aria-hidden": "true"
                  })),
                  _createTextVNode(" " + _toDisplayString(renewalStatusLabel.value), 1)
                ]),
                _: 1
              }, 8, ["class"]),
              (__props.canEdit)
                ? (_openBlock(), _createBlock(_unref(Button), {
                    key: 0,
                    variant: "outline",
                    size: "sm",
                    class: "border-blue-200 text-blue-700 hover:border-blue-300 hover:bg-blue-50",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('edit')))
                  }, {
                    default: _withCtx(() => [
                      _cache[5] || (_cache[5] = _createTextVNode(" Edit renewal ", -1)),
                      _createVNode(_unref(Pencil), {
                        class: "ml-2 size-3.5",
                        "aria-hidden": "true"
                      })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_unref(Button), {
                variant: "outline",
                size: "sm",
                class: "border-slate-200 text-slate-600 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('open-profile')))
              }, {
                default: _withCtx(() => [
                  _cache[6] || (_cache[6] = _createTextVNode(" Full profile ", -1)),
                  _createVNode(_unref(ExternalLink), {
                    class: "ml-2 size-3.5",
                    "aria-hidden": "true"
                  })
                ]),
                _: 1
              })
            ])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("div", _hoisted_13, [
              (__props.isError)
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                    _cache[8] || (_cache[8] = _createElementVNode("span", null, "Some account details could not be loaded.", -1)),
                    _createVNode(_unref(Button), {
                      variant: "ghost",
                      size: "sm",
                      class: "text-amber-800 hover:bg-amber-100",
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('retry')))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(RefreshCw), {
                          class: "mr-2 size-3.5",
                          "aria-hidden": "true"
                        }),
                        _cache[7] || (_cache[7] = _createTextVNode(" Retry ", -1))
                      ]),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true),
              (__props.isLoading)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(7, (item) => {
                      return _createElementVNode("div", {
                        key: item,
                        class: "h-24 animate-pulse rounded-lg bg-slate-200/70"
                      })
                    }), 64))
                  ]))
                : (_openBlock(), _createElementBlock("section", _hoisted_16, [
                    _createElementVNode("div", _hoisted_17, [
                      _createVNode(_unref(CalendarDays), {
                        class: "size-4 text-indigo-500",
                        "aria-hidden": "true"
                      }),
                      _cache[9] || (_cache[9] = _createElementVNode("h2", { class: "text-sm font-bold text-slate-700" }, " Renewal milestones ", -1))
                    ]),
                    _createElementVNode("div", _hoisted_18, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(renewalMilestones.value, (milestone) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: milestone.label,
                          class: "rounded-lg border border-slate-100 bg-slate-50 p-3"
                        }, [
                          _createElementVNode("p", _hoisted_19, _toDisplayString(milestone.label), 1),
                          _createElementVNode("p", {
                            class: _normalizeClass(["mt-2 text-sm font-semibold", milestoneClass(milestone.value)])
                          }, _toDisplayString(milestone.value || "Not scheduled"), 3)
                        ]))
                      }), 128))
                    ])
                  ])),
              _createElementVNode("section", _hoisted_20, [
                _createElementVNode("div", _hoisted_21, [
                  _createVNode(_unref(FileClock), {
                    class: "size-4 text-indigo-500",
                    "aria-hidden": "true"
                  }),
                  _cache[10] || (_cache[10] = _createElementVNode("h2", { class: "text-sm font-bold text-slate-700" }, " Renewal note ", -1))
                ]),
                _createElementVNode("p", _hoisted_22, _toDisplayString(renewalNote.value || "No renewal note recorded."), 1)
              ]),
              _createVNode(RenewalActivityLog, {
                history: __props.detailClient?.renewal_history,
                "is-loading": __props.isLoading,
                "is-unavailable": __props.isError || !__props.detailClient
              }, null, 8, ["history", "is-loading", "is-unavailable"])
            ])
          ])
        ]))
  ]))
}
}

})